import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { makePair, Pair } from '../../../../../adl-gen/sys/types';
import { AnswerScores, I18nContent } from '../../../../../adl-gen/totalinteraction/futurefit/db';
import { LangCode, withI18n, WithI18nProps } from '../../../../../internationalization/i18n-hoc';
import { SelectBox, SelectOption } from '../../../../../ui/widgets/select/select';
import { Table, TableCell, TableRow } from '../../../../../ui/widgets/table/table';
import {
  FieldState,
  FormValue,
  newIntegerFieldState,
  withValueCallback
} from '../../../../../ui/widgets/text-input/text-field';
import { WithId } from '../../../../../util/utils';
import { AnswerType, isEmpty, TextInputObserver } from '../../../common/editor';

const styles = require('../../../common/editor.css');
const listStyles = require('../../../common/list.css');
const fonts = require('../../../../../ui/common/fonts.css');

/// TODO Amalgamate/consolidate all these type defs and interface defs
type SingleResponseInputScore = WithId<Pair<number, FieldState<number>>>;

interface SingleResponseInputScores {
  scores: SingleResponseInputScore[];
  defaultScore: FieldState<number>;
}

interface ScoringInput {
  kind: AnswerType;
  value: SingleResponseInputScores;
}

export interface SingleResponseEditorProps extends WithI18nProps {
  debug?: boolean;
  lang?: LangCode;
  name: string;
  content: I18nContent;
  answers: WithId<I18nContent>[];
  scoring?: AnswerScores;

  postUpdates(answers: AnswerScores): void;

  onCancel(): void;
}

/**
 * Base component to create/update SingleResponse answers.
 * To impose some sense of order in this, we will be opinionated about the score
 * map. So the index in the scores array will always equal the left-hand value
 * of the corresponding Pair for the score
 * ie. [score0, score1, score2, ...] -> [ {v1: 0, v2: score-for-answer-0-if-any}, {v1: 1, v2: score-for-answer-1-if-any}, {v1: 2, ...}, ... ]
 */
@observer
class SingleResponseEditor0 extends React.Component<SingleResponseEditorProps> {
  @observable inputScores: ScoringInput;

  @observable lang: LangCode;

  constructor(props: Readonly<SingleResponseEditorProps>) {
    super(props);
    this.lang = props.lang ? props.lang : props.currentLangCode();
    const { answers, scoring } = props;

    const scores: SingleResponseInputScore[] = [];

    if (scoring) {
      if (scoring.kind !== 'singleResponse') {
        // Something has gone wrong
        throw new Error(`SingleResponseEditor: incompatible answer type ${scoring.kind}`);
      }
    }

    answers.map((answer, index) => {
      /// Possibly sparse array of scores... at most one per answer...
      /// We'll push that into a packed array including possibly empty scores
      /// The ID is required to match answers while they are in memory, in case they
      /// move around or are deleted - keep the scores with the answers
      const inputScore = {
        id: answer.id,
        value: makePair({
          v1: index,
          v2: withValueCallback(newIntegerFieldState(0, 100), this.onChange)
        })
      };

      scores.push(inputScore);
    });
    /// Scoring is now automatically sorted

    const defaultScore = withValueCallback(newIntegerFieldState(0, 100), this.onChange);

    this.inputScores = {
      kind: 'singleResponse',
      value: {
        scores,
        defaultScore
      }
    };
    /// Now initialize - this triggers onSave so it's important the structure is
    /// complete before we initialize
    if (scoring && scoring.kind === 'singleResponse') {
      this.inputScores.value.defaultScore.onChange(`${scoring.value.defaultScore}`);
      scoring.value.scores.map(score => {
        /// score.v1 = index into answer array, not answer ID
        const inputScore = this.inputScores.value.scores.find(sc => sc.value.v1 === score.v1);
        if (inputScore) {
          /// Should be of course
          inputScore.value.v2.onChange(`${score.v2}`);
        }
      });
    }
  }

  @action
  changeLang = (opt: SelectOption) => {
    this.lang = opt.value as LangCode;
  };

  @action
  onChange = (_score: FormValue<number>) => {
    const scoresToPost: AnswerScores = { kind: 'singleResponse', value: { defaultScore: 33, scores: [] } };
    /// Get the input value in full - have to run this filter
    if (this.inputScores.kind === 'singleResponse') {
      if (isEmpty(this.inputScores.value.defaultScore)) {
        scoresToPost.value.defaultScore = 0;
      } else {
        const ds = this.inputScores.value.defaultScore.getValid();
        if (ds !== undefined) {
          scoresToPost.value.defaultScore = ds;
        }
      }
      // AnswerScore: Reset the whole lot - this is because "splicing" is
      // unreliable when each keystroke can update a target value, and answers
      // cann be reordered or deleted
      const scores = this.inputScores.value.scores.reduce((acc: Pair<number, number>[], next, idx) => {
        const v2 = next.value.v2.getValid();
        if (v2 !== undefined) {
          /// Update v1 in case answers reordered since last time
          acc.push(makePair({ v1: idx, v2 }));
        }
        return acc;
      }, []);
      scoresToPost.value.scores = scores;
    }
    /// Post
    this.onSave(scoresToPost);
  };

  LangSelector = observer(() => {
    const { i18nOptions } = this.props;
    const onChange = this.changeLang;
    const options = i18nOptions();
    return (
      <SelectBox
        options={options}
        defaultValue={options.find(option => (option.value as LangCode) === this.lang)}
        onChange={onChange}
        title={'Language'}
        xsSpan={2}
      />
    );
  });

  DefaultScoreObserver = observer(() =>
    TextInputObserver({
      field: this.inputScores.value.defaultScore,
      type: 'number',
      title: 'Default score',
      placeholder: 'eg. 33',
      xsSpan: 1,
      debug: this.props.debug
    })
  );

  AnswerScoreObserver = observer((prop: { idx: number }) =>
    TextInputObserver({
      field: this.inputScores.value.scores[prop.idx].value.v2,
      type: 'number',
      title: '', //`Score for answer ${prop.v1 + 1}`,
      placeholder: '', //"eg. 33",
      xsSpan: 2,
      debug: this.props.debug
    })
  );

  @computed
  get inputIsValid(): boolean {
    let isValid: boolean = true;
    // const { answers } = this.props
    // const { defaultScore, scores } = this.inputScores.value
    // if (this.inputScores.kind !== "singleResponse") { return false }

    // if (defaultScore.getValid() === undefined ||
    //     isEmpty(defaultScore) ||
    //     !isNumeric(defaultScore.value())
    //   ) { return false }
    // if (answers.length > 0) {
    //   // Should never happen
    //   if (scores.length !== answers.length) { return false }
    //   isValid = scores.every(score => {
    //     if (score.v2.getValid() === undefined) { return false }
    //     return (isEmpty(score.v2) || isNumeric(score.v2.value()))
    //   })
    // }
    return isValid;
  }

  @action
  onSave = (scoresToPost: AnswerScores) => {
    const { postUpdates } = this.props;
    if (this.inputIsValid) {
      postUpdates(scoresToPost);
    }
  };

  renderHeaderRow = () => {
    return (
      <TableRow header>
        <TableCell>
          <div>ANSWER TEXT</div>
        </TableCell>
        <TableCell>
          <div>ANSWER SCORE</div>
        </TableCell>
      </TableRow>
    );
  };

  Edit = () => {
    const { answers, content, t } = this.props;
    return (
      <div className={styles.page}>
        <div className={styles.body}>
          <div className={styles.row}>
            <this.LangSelector />
          </div>
          <div className={styles.row}>
            <span className={fonts.caption}>{t(content, this.lang)}</span>
          </div>
          <div className={styles.row}>
            <this.DefaultScoreObserver />
          </div>
          <div className={styles.row}></div>
          <Table column3VisibleAfter="sm" className={listStyles.noBorder}>
            {this.renderHeaderRow()}
            {answers.map(answer => {
              const idx = this.inputScores.value.scores.findIndex(score => score.id === answer.id);
              if (idx >= 0) {
                /// Should always do
                return (
                  <TableRow key={answer.id}>
                    <TableCell>
                      <span className={fonts.caption}>{t(answer.value, this.lang)}</span>
                    </TableCell>
                    <TableCell>
                      <this.AnswerScoreObserver idx={idx} />
                    </TableCell>
                  </TableRow>
                );
              } else {
                return null;
              }
            })}
          </Table>
        </div>
      </div>
    );
  };

  render() {
    const { name } = this.props;
    const title = `Answers for question ${name} (${this.inputScores.kind})`;
    return (
      <React.Fragment>
        <h4>{title}</h4>
        <this.Edit />
      </React.Fragment>
    );
  }
}

export const SingleResponseEditor = withI18n(SingleResponseEditor0);



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/answers-scoring-editors/single-response-scores-editor.tsx